/**
 * EID-125: Datamesh-schema-exact per-input query specification for transforms.
 *
 * `dataSpec.inputs` entries are Datamesh Query objects ({ datasource, plus
 * optional variables/timefilter/geofilter/coordfilter/aggregate/crs }) instead
 * of bare source-id strings, so a transform can control the query each input
 * is resolved with. Per-field merge against the propagated consumer query:
 * present → override, absent → inherit, explicit null → drop.
 *
 * Extension over the Datamesh schema: `timefilter.times` entries additionally
 * accept signed ISO 8601 durations ('-PT24H'), resolved against the queried
 * time T (the consumer timefilter's times[0]) at rewrite time — the same idiom
 * as timeControl.range. `null` bounds are open-ended. When the consumer query
 * has no timefilter (e.g. the init seed query {}), a duration-anchored window
 * cannot resolve; the timefilter is omitted entirely (full extent, matching
 * seed behaviour).
 *
 * Datamesh forms the client engine cannot execute yet — timefilter type
 * 'series'/'trajectory', timefilter resolution/resample, non-bbox geofilters,
 * geofilter interp/resolution/alltouched, coordfilter, aggregate, crs — are
 * rejected with a thrown descriptive error at normalisation (transform init),
 * never silently null-ed (setting any of them to null is always legal: it
 * drops the consumer's field). Unknown fields, including inside timefilter
 * and geofilter, are rejected for the same reason.
 *
 * Kept dependency-free of the framework/utils barrels (dayjs + duration
 * plugin only, same as data-model.ts) so it can be unit tested per the repo
 * testing note.
 */
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

/**
 * Datamesh TimeFilter, extended so `times` entries may be signed ISO 8601
 * durations relative to the queried time (resolved at rewrite time) or null
 * (open-ended bound).
 */
export interface TransformInputTimeFilter {
  type?: string;
  times: (string | null)[];
  resolution?: string | null;
  resample?: string | null;
}

/** Datamesh GeoFilter (only the bbox form is executable client-side). */
export interface TransformInputGeoFilter {
  type?: string;
  geom: number[] | object;
  resolution?: number | null;
  alltouched?: boolean | null;
  // Client-side extension: bbox subsampling limit (see build_geo_selectors).
  maxGridSize?: number;
  interp?: unknown;
}

/**
 * One `dataSpec.inputs` entry: a Datamesh Query whose datasource is the id of
 * another data source in the spec. Fields set to null drop the corresponding
 * field of the propagated consumer query.
 */
export interface TransformInputSpec {
  datasource: string;
  variables?: string[] | null;
  timefilter?: TransformInputTimeFilter | null;
  geofilter?: TransformInputGeoFilter | null;
  coordfilter?: { coord: string; values: (string | number)[] }[] | null;
  aggregate?: object | null;
  crs?: number | string | null;
}

// Must match the data source `id` pattern in schemas/src/eidos/data.json.
const DATASOURCE_ID_PATTERN = /^[A-Za-z0-9_-]+$/;

// Signed ISO 8601 duration (the timeControl.range idiom: 'PT0H', '-P1D').
const DURATION_PATTERN = /^[+-]?P/;

const KNOWN_FIELDS = new Set([
  'datasource',
  'variables',
  'timefilter',
  'geofilter',
  'coordfilter',
  'aggregate',
  'crs',
]);

// Sub-field allowlists: an unknown key (a typo, or a runtime-only extension
// like the nearest-filter 'tolerance') would otherwise be silently dropped by
// the rewrite — the silent-misresolution class this module exists to reject.
const TIMEFILTER_FIELDS = new Set(['type', 'times', 'resolution', 'resample']);
// maxGridSize is a client-side extension build_geo_selectors executes
// (bbox subsampling), so it is allowed alongside the Datamesh fields.
const GEOFILTER_FIELDS = new Set([
  'type',
  'geom',
  'resolution',
  'alltouched',
  'maxGridSize',
]);

const isDurationString = (value: unknown): value is string =>
  typeof value === 'string' && DURATION_PATTERN.test(value.trim());

/**
 * Validate one (already object-form) input spec. Throws a descriptive Error
 * naming the offending field for any Datamesh form the client engine cannot
 * execute yet, or for a malformed entry. `label` identifies the input in the
 * error message (e.g. "inputs[1] ('waves')").
 */
export function validateTransformInput(
  input: TransformInputSpec,
  label: string,
): void {
  const fail = (message: string): never => {
    throw new Error(`Transform ${label}: ${message}`);
  };

  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    fail(`must be an object ({"datasource": "<data-id>", ...query fields})`);
  }
  if (typeof input.datasource !== 'string' || input.datasource === '') {
    fail(`'datasource' is required and must be the id of another data source`);
  }
  if (!DATASOURCE_ID_PATTERN.test(input.datasource)) {
    fail(
      `'datasource' value '${input.datasource}' is not a valid data source id (must match ${DATASOURCE_ID_PATTERN})`,
    );
  }
  for (const key of Object.keys(input)) {
    if (!KNOWN_FIELDS.has(key)) {
      fail(
        `unknown query field '${key}' (supported: ${[...KNOWN_FIELDS].join(', ')})`,
      );
    }
  }

  // null is always legal for the fields below: it drops the consumer's field.
  const { variables, timefilter, geofilter, coordfilter, aggregate, crs } =
    input;

  if (variables !== undefined && variables !== null) {
    if (
      !Array.isArray(variables) ||
      variables.some((v) => typeof v !== 'string')
    ) {
      fail(`'variables' must be an array of variable names, or null`);
    }
  }

  if (timefilter !== undefined && timefilter !== null) {
    for (const key of Object.keys(timefilter)) {
      if (!TIMEFILTER_FIELDS.has(key)) {
        fail(
          `unknown 'timefilter' field '${key}' (supported: ${[...TIMEFILTER_FIELDS].join(', ')})`,
        );
      }
    }
    const type = timefilter.type ?? 'range';
    if (type !== 'range') {
      fail(
        `'timefilter.type' '${type}' is not executable by the client engine yet — only 'range' is supported`,
      );
    }
    if (timefilter.resolution != null && timefilter.resolution !== 'native') {
      fail(
        `'timefilter.resolution' is not executable by the client engine yet`,
      );
    }
    if (timefilter.resample != null) {
      fail(`'timefilter.resample' is not executable by the client engine yet`);
    }
    if (!Array.isArray(timefilter.times) || timefilter.times.length !== 2) {
      fail(`'timefilter.times' must be a [start, end] array for type 'range'`);
    }
    for (const t of timefilter.times) {
      if (t === null) continue; // open-ended bound
      if (typeof t !== 'string') {
        fail(
          `'timefilter.times' entries must be ISO 8601 date-time or duration strings, or null`,
        );
      }
      if (isDurationString(t)) {
        const ms = dayjs
          .duration(t.trim().replace(/^[+-]/, ''))
          .asMilliseconds();
        if (!Number.isFinite(ms)) {
          fail(
            `'timefilter.times' entry '${t}' is not a valid ISO 8601 duration`,
          );
        }
      } else if (!dayjs(t).isValid()) {
        fail(
          `'timefilter.times' entry '${t}' is not a valid ISO 8601 date-time or duration`,
        );
      }
    }
  }

  if (geofilter !== undefined && geofilter !== null) {
    if (typeof geofilter !== 'object' || Array.isArray(geofilter)) {
      fail(`'geofilter' must be a Datamesh GeoFilter object, or null`);
    }
    if (geofilter.interp !== undefined) {
      fail(`'geofilter.interp' is not executable by the client engine yet`);
    }
    for (const key of Object.keys(geofilter)) {
      if (!GEOFILTER_FIELDS.has(key)) {
        fail(
          `unknown 'geofilter' field '${key}' (supported: ${[...GEOFILTER_FIELDS].join(', ')})`,
        );
      }
    }
    const gtype = geofilter.type ?? 'bbox';
    if (gtype !== 'bbox') {
      fail(
        `'geofilter.type' '${gtype}' is not executable by the client engine yet — only 'bbox' is supported`,
      );
    }
    // Datamesh extraction knobs the client's local index selection cannot
    // honour (resolution 0 is the schema default meaning "native").
    if (geofilter.resolution != null && geofilter.resolution !== 0) {
      fail(`'geofilter.resolution' is not executable by the client engine yet`);
    }
    if (geofilter.alltouched != null) {
      fail(`'geofilter.alltouched' is not executable by the client engine yet`);
    }
    if (
      !Array.isArray(geofilter.geom) ||
      geofilter.geom.length !== 4 ||
      geofilter.geom.some((n) => typeof n !== 'number')
    ) {
      fail(`'geofilter.geom' must be a [x0, y0, x1, y1] bbox array`);
    }
  }

  if (coordfilter !== undefined && coordfilter !== null) {
    fail(`'coordfilter' is not executable by the client engine yet`);
  }
  if (aggregate !== undefined && aggregate !== null) {
    fail(`'aggregate' is not executable by the client engine yet`);
  }
  if (crs !== undefined && crs !== null) {
    fail(`'crs' is not executable by the client engine yet`);
  }
}

/**
 * Normalise a transform's `inputs` at the InitTransformSpec boundary:
 * - legacy string entries become `{ datasource: <id> }` (deprecation warning);
 * - every entry is validated (throws on forms the client cannot execute);
 * - duplicate `datasource` values are legal (same source, different queries)
 *   but collide on the named `$inputs` key — the LAST duplicate wins, so a
 *   warning points authors at positional access.
 *
 * `opts.warnings: false` silences the deprecation/duplicate console.warns
 * while validation still throws. The data provider normalizes once per
 * watcher and owns the warnings; the re-normalization inside Transform.init
 * re-runs on every worker (re)init and passes false so the warnings are not
 * repeated per re-init.
 */
export function normalizeTransformInputs(
  inputs: ReadonlyArray<string | TransformInputSpec> | undefined | null,
  opts?: { warnings?: boolean },
): TransformInputSpec[] {
  if (!inputs) return [];
  const warnings = opts?.warnings !== false;
  const normalized = inputs.map((input, i) => {
    let spec: TransformInputSpec;
    if (typeof input === 'string') {
      if (warnings) {
        console.warn(
          `Transform inputs: string entry '${input}' is deprecated — use {"datasource": "${input}"} (EID-125).`,
        );
      }
      spec = { datasource: input };
    } else {
      spec = input;
    }
    const label =
      typeof input === 'string' || typeof input?.datasource !== 'string'
        ? `inputs[${i}]`
        : `inputs[${i}] ('${input.datasource}')`;
    validateTransformInput(spec, label);
    return spec;
  });

  if (!warnings) return normalized;

  const byId = new Map<string, number[]>();
  normalized.forEach((spec, i) => {
    const positions = byId.get(spec.datasource) ?? [];
    positions.push(i);
    byId.set(spec.datasource, positions);
  });
  for (const [id, positions] of byId) {
    if (positions.length > 1) {
      console.warn(
        `Transform inputs: duplicate datasource '${id}' at positions [${positions.join(', ')}] — ` +
          `the named $inputs['${id}'] key resolves to the last one; use positional access ($inputs[${positions.join('], $inputs[')}]) to disambiguate.`,
      );
    }
  }
  return normalized;
}

/**
 * Resolve one `times` entry against the anchor time T: signed ISO durations
 * become absolute ISO date-times relative to T, date-time strings pass
 * through verbatim, null stays null (open-ended bound).
 */
const resolveTimeEntry = (
  entry: string | null,
  anchor: unknown,
): string | null => {
  if (entry === null) return null;
  if (!isDurationString(entry)) return entry;
  const trimmed = entry.trim();
  const negative = trimmed.startsWith('-');
  const dur = dayjs.duration(trimmed.replace(/^[+-]/, ''));
  const base = dayjs(anchor as string | number | Date);
  return (negative ? base.subtract(dur) : base.add(dur)).toISOString();
};

/**
 * Resolve an input timefilter override against the consumer query's
 * timefilter. Returns the Datamesh-schema-exact runtime timefilter, or
 * undefined when a duration-anchored window has no anchor to resolve against
 * (consumer query without a timefilter, e.g. the init seed query {}) — the
 * caller omits the timefilter entirely (full extent).
 */
const resolveTimeFilter = (
  override: TransformInputTimeFilter,
  consumerTimeFilter: { times?: unknown[] } | undefined | null,
): { type: string; times: (string | null)[] } | undefined => {
  const anchor = consumerTimeFilter?.times?.[0];
  const needsAnchor = override.times.some(isDurationString);
  if (needsAnchor && anchor == null) return undefined;
  return {
    type: override.type ?? 'range',
    times: override.times.map((t) => resolveTimeEntry(t, anchor)),
  };
};

const MERGE_FIELDS = [
  'variables',
  'timefilter',
  'geofilter',
  'coordfilter',
  'aggregate',
  'crs',
] as const;

/**
 * Rewrite the propagated consumer query for one input, per-field:
 * present on the input spec → override, absent → inherit, explicit null →
 * drop. Non-Datamesh runtime fields on the consumer query (e.g. levelfilter)
 * always inherit. Does not mutate `consumerQuery`; `$query` inside the worker
 * remains the original consumer query.
 */
export function rewriteInputQuery(
  consumerQuery: Record<string, unknown> | null | undefined,
  inputSpec: TransformInputSpec,
): Record<string, unknown> {
  const query: Record<string, unknown> = { ...(consumerQuery ?? {}) };
  for (const field of MERGE_FIELDS) {
    if (!(field in inputSpec)) continue; // absent → inherit
    const override = inputSpec[field];
    if (override === null) {
      delete query[field]; // explicit null → drop
      continue;
    }
    if (field === 'timefilter') {
      const resolved = resolveTimeFilter(
        override as TransformInputTimeFilter,
        (consumerQuery as { timefilter?: { times?: unknown[] } } | undefined)
          ?.timefilter,
      );
      if (resolved === undefined) delete query.timefilter;
      else query.timefilter = resolved;
    } else {
      query[field] = override;
    }
  }
  return query;
}
